import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BrainCircuit, Target, Users, FileText, TrendingUp, ShieldCheck } from "lucide-react";
import { MatchRing } from "@/components/MatchRing";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { PricingSection } from "@/components/PricingSection";
import { TeamSection } from "@/components/TeamSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { SEO } from "@/components/SEO";

export const Landing = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const avg = stats?.avg_match ?? 0;
  const hi = stats?.high_match_pct ?? 0;
  const apps = stats?.applications ?? 0;
  const jobs = stats?.jobs ?? 0;
  return (
    <div>
      <SEO
        title="Hire on signal, not on résumés"
        description="Axiony scores every applicant against every role in real time using Gemini 3 Flash. Candidates see their fit before applying. Recruiters see ranked pipelines instantly."
        path="/"
        image="https://axiony.co.in/api/og/social-proof.png"
      />
      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 radial-fade pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="mono-label mb-6 flex items-center gap-2" data-testid="hero-eyebrow">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#06B6D4] pulse-ring" />
                LLM-driven talent matching
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-white">
                Hire on <span className="text-[#06B6D4]">signal</span>,<br />
                not on résumés.
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
                Axiony scores every applicant against every role in real time — surfacing strengths,
                exposing gaps, and ranking candidates the way a senior hiring manager would.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate("/signup")}
                  data-testid="hero-cta-primary"
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6 py-6 text-base"
                >
                  Start free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  data-testid="hero-cta-secondary"
                  className="rounded-full px-6 py-6 text-base bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white"
                >
                  Sign in
                </Button>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl" data-testid="hero-stats">
                <StatPie label="Avg. match score" value={avg} display={`${avg}%`} color="#06B6D4" note="across all applications" testId="hero-stat-avg" />
                <StatPie label="High-quality matches" value={hi} display={`${hi}%`} color="#10B981" note="scored 80+/100" testId="hero-stat-high" />
                <StatPie label="Talent pool" value={Math.min(100, apps * 8)} display={apps.toLocaleString()} color="#3B82F6" note={`applications · ${jobs} open roles`} testId="hero-stat-apps" />
              </div>
            </div>

            {/* Preview card */}
            <div className="lg:col-span-5">
              <div className="bg-[#111827] border border-white/10 rounded-xl p-6 relative">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="mono-label">match report</div>
                    <div className="font-display text-lg font-medium mt-1">Senior ML Engineer</div>
                    <div className="text-sm text-slate-400">Northwind Labs · Remote</div>
                  </div>
                  <MatchRing value={87} size={84} stroke={8} />
                </div>
                <div className="space-y-3">
                  <Row label="Skills" v={92} />
                  <Row label="Experience" v={88} />
                  <Row label="Culture fit" v={81} />
                </div>
                <div className="mt-6 border-t border-white/10 pt-4">
                  <div className="mono-label mb-2 text-[#06B6D4]">ai summary</div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Deep PyTorch background aligns with model ownership. Missing production
                    MLOps experience — mentorable given seniority of the team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mono-label mb-4">why axiony</div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
          Every résumé, understood in seconds.
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-4">
          <Feature icon={BrainCircuit} title="Reasoned matching" body="Gemini 3 Flash reads every résumé and scores signal against real job intent — not keywords." />
          <Feature icon={Target} title="Explainable scores" body="Skills, experience, culture fit — with strengths & gaps you can act on." />
          <Feature icon={Users} title="Two sides, one truth" body="Candidates see their fit before applying. Recruiters see ranked pipelines instantly." />
          <Feature icon={FileText} title="Any résumé format" body="Paste text or drop a PDF/DOCX. Axiony extracts what matters." />
          <Feature icon={TrendingUp} title="Rank by fit" body="Applicants sorted by match score. Focus attention where it counts." />
          <Feature icon={ShieldCheck} title="Fair by default" body="Standardized scoring reduces bias by evaluating everyone on the same axes." />
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Team */}
      <TestimonialsSection />
      <TeamSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="rounded-2xl border border-white/10 bg-[#111827] p-12 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 radial-fade pointer-events-none" />
          <div className="relative">
            <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-xl">
              Stop reading résumés. Start reading signal.
            </h3>
            <p className="mt-4 text-slate-300 max-w-xl">
              Free while in beta. Post jobs, upload résumés, and see match scores in under a minute.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/signup">
                <Button data-testid="cta-signup" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6 py-6">
                  Create free account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div>© 2026 Axiony. Match on signal.</div>
          <a
            href="mailto:medhachakraborty@axiony.co.in"
            data-testid="footer-contact-email"
            className="text-slate-400 hover:text-[#06B6D4] transition-colors"
          >
            medhachakraborty@axiony.co.in
          </a>
          <div className="mono-label">v0.1 · beta</div>
        </div>
      </footer>
    </div>
  );
};

const Stat = ({ label, value, note }) => (
  <div>
    <div className="font-display text-2xl font-semibold text-white">{value}</div>
    <div className="text-xs text-slate-400 mt-1">
      {label} {note && <span className="text-slate-500">· {note}</span>}
    </div>
  </div>
);

const StatPie = ({ label, value, display, color, note, testId }) => {
  const data = [
    { name: "value", v: value },
    { name: "rest", v: 100 - value },
  ];
  return (
    <div className="flex flex-col items-start" data-testid={testId}>
      <div className="relative w-24 h-24">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="v"
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={44}
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive
              animationDuration={1000}
            >
              <Cell fill={color} />
              <Cell fill="#1F2937" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="font-display text-lg font-semibold" style={{ color }}>
            {display || `${value}%`}
          </span>
        </div>
      </div>
      <div className="text-xs text-slate-300 mt-3 font-medium">{label}</div>
      {note && <div className="text-[11px] text-slate-500 mt-0.5">{note}</div>}
    </div>
  );
};

const Row = ({ label, v }) => (
  <div>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-200 font-medium">{v}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${v}%`,
          background: v >= 80 ? "#10B981" : v >= 60 ? "#F59E0B" : "#EF4444",
        }}
      />
    </div>
  </div>
);

const Feature = ({ icon: Icon, title, body }) => (
  <div className="border border-white/10 bg-[#111827] rounded-xl p-6 hover:border-white/20 hover:-translate-y-0.5 transition-transform">
    <Icon className="w-6 h-6 text-[#06B6D4]" strokeWidth={1.75} />
    <div className="font-display text-lg font-medium mt-4">{title}</div>
    <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>
  </div>
);
